// Copyright 2023 Sorama B.V.
import { customElement } from "aurelia-framework";
import { Chart, PointStyle } from "chart.js";
import { Point2D } from "datatypes/SoramaLocalApi";
import { SLRData } from "datatypes/SpecialMeasurement";
import { ReportTemplate } from "../ReportTemplate";

@customElement("slr-report-template")
export class SlrReportTemplate extends ReportTemplate<SLRData> {
    private chartRef: HTMLCanvasElement;

    private slrTable: Point2D[] = [];
    private xAxisFreq: number[] = [125, 250, 500, 1000, 2000, 4000, 8000];
    private yAxisReduction: number[];

    public reportChanged() {
        if (!this.report) return;

        this.cleanUpUIView();

        this.yAxisReduction = this.data.speechLevelReduction;

        this.yAxisReduction.forEach((yAxisreport: number, index: number) => {
            this.slrTable.push({ x: this.xAxisFreq[index], y: yAxisreport });
        });
    }

    attached() {
        this.drawChart(this.xAxisFreq, this.yAxisReduction);
    }

    private cleanUpUIView(): void {
        this.slrTable.splice(0, this.slrTable.length);
    }

    public setBlack(l: number, r: number): boolean {
        return this.data.weightedSpeechLevelReduction > l && this.data.weightedSpeechLevelReduction <= r;
    }

    protected drawChart(xAxisFreq: number[], yAxisReduction: number[]): void {
        const yLabel = "Speech level reduction [dB]";

        // Get y-axis limits from data bounds (rounded to nearest 5)
        const minValue = Math.min(...yAxisReduction);
        const maxValue = Math.max(...yAxisReduction);

        const minBound = 5 * (minValue > 0 ? Math.ceil(minValue / 5) : Math.floor(minValue / 5));
        const maxBound = 5 * (maxValue > 0 ? Math.ceil(maxValue / 5) : Math.floor(maxValue / 5));

        try {
            new Chart(this.chartRef, {
                type: "line",
                data: {
                    labels: xAxisFreq,
                    datasets: [
                        {
                            label: "Speech level reduction",
                            fill: false,
                            lineTension: 0,
                            pointStyle: {} as PointStyle,
                            pointRadius: 1,
                            data: yAxisReduction,
                            backgroundColor: "black",
                            borderColor: "black",
                            borderWidth: 2,
                        },
                    ],
                },
                options: {
                    animation: {
                        duration: 0,
                    },
                    aspectRatio: 1 / 1.2,
                    legend: { display: false },
                    responsive: false,
                    maintainAspectRatio: false,
                    scales: {
                        xAxes: [
                            {
                                scaleLabel: {
                                    display: true,
                                    labelString: "Frequency [Hz]",
                                },
                                offset: true, // Start from 0
                                ticks: {
                                    autoSkip: false,
                                    maxRotation: 0,
                                    minRotation: 0,
                                    display: true,
                                    beginAtZero: true,
                                },
                            },
                        ],
                        yAxes: [
                            {
                                ticks: {
                                    // Always include Y = 0 line
                                    min: Math.min(0, minBound),
                                    max: Math.max(0, maxBound),
                                },
                                scaleLabel: {
                                    display: true,
                                    labelString: yLabel,
                                },
                            },
                        ],
                    },
                },
            });
        } catch (error) {
            console.log(error);
        }
    }
}
